import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { PromptTypes } from '@kinde/js-utils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoginImage from '@/assets/login_background.png';
import bquestLogo from '@/assets/bquest-logo.png';
import { useAuth } from '../hooks/useAuth';
import { AuthStepPanel } from './AuthStepPanel';
import { CompanyAuthMethodsCard } from './CompanyAuthMethodsCard';
import { getPartnerCompanyLoginInfo } from '../api/partnerCompanyLoginApi';

const EMAIL_CONNECTION_ID = import.meta.env.VITE_KINDE_EMAIL_CONNECTION_ID;
const AUTH_INTENT_KEY = 'bquest_auth_intent';

const getDashboardPath = (role: string) => {
  return role === 'SERVICE_PROVIDER' ? '/dashboard/service-provider' : '/dashboard';
};

export const PartnerCompanyLoginPage = () => {
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState('');
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const { companySlug = '' } = useParams<{ companySlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { login: kindeLogin, isLoading: kindeLoading } = useKindeAuth();
  const hasNavigatedRef = useRef(false);

  const { data: company, isLoading, isError } = useQuery({
    queryKey: ['partner-company-login', companySlug],
    queryFn: () => getPartnerCompanyLoginInfo(companySlug),
    enabled: companySlug.trim().length > 0,
    retry: false,
  });

  useEffect(() => {
    if (user && !hasNavigatedRef.current) {
      navigate(getDashboardPath(user.role));
      hasNavigatedRef.current = true;
    }
  }, [navigate, user]);

  const enabledAuthMethods = useMemo(
    () => new Set(company?.authMethods.filter((entry) => entry.isEnabled).map((entry) => entry.method)),
    [company],
  );
  const hasExplicitOtp = enabledAuthMethods.has('OTP');
  const showSso = enabledAuthMethods.has('SSO');
  const showOtp = hasExplicitOtp || !showSso;
  const normalizedEmail = email.trim().toLowerCase();

  const handleOtpLogin = async () => {
    if (!showOtp || isSubmittingOtp || kindeLoading) {
      return;
    }

    if (!normalizedEmail) {
      setAuthError('Email address is required.');
      return;
    }

    setIsSubmittingOtp(true);

    try {
      setAuthError('');
      sessionStorage.setItem(
        AUTH_INTENT_KEY,
        JSON.stringify({
          method: 'otp',
          email: normalizedEmail,
          companySlug: company?.companySlug ?? companySlug,
          expectedCompanySlug: company?.companySlug ?? companySlug,
        }),
      );

      await kindeLogin({
        connectionId: EMAIL_CONNECTION_ID,
        loginHint: normalizedEmail,
        prompt: PromptTypes.login,
      });
    } catch {
      setAuthError('Authentication failed. Please try again.');
    } finally {
      setIsSubmittingOtp(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="min-h-screen bg-[#026857]">
        <header className="sticky top-0 z-50 border-b border-[#025246] bg-[#026857]">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="transition-opacity hover:opacity-80">
                <img src={bquestLogo} alt="bQuest" className="h-8 brightness-0 invert" />
              </Link>
              <Link
                to="/login"
                className="rounded-md bg-white/20 px-3 py-2 font-termina text-sm font-medium uppercase text-white transition-colors"
              >
                Default Login
              </Link>
            </div>
          </div>
        </header>

        <main className="md:container flex min-h-[calc(100svh-72px)] items-center gap-10 px-4 py-6 max-md:flex-col md:gap-12 md:px-6">
          <section className="w-full max-w-[32rem] py-2">
            {isLoading && (
              <AuthStepPanel
                title="Preparing sign-in"
                description="Loading your company options."
                animationKey="partner-company-loading"
              >
                <p className="font-termina text-white">Loading company login...</p>
              </AuthStepPanel>
            )}

            {isError && (
              <AuthStepPanel
                title="Company login unavailable"
                description="We could not load this company page."
                animationKey="partner-company-error"
              >
                <p className="font-termina text-white">
                  We couldn&apos;t find that company login page. Please double-check the link or use the
                  default login instead.
                </p>
              </AuthStepPanel>
            )}

            {company && (
              <CompanyAuthMethodsCard
                animationKey={`partner-company-${company.companySlug ?? companySlug}`}
                companyName={company.name}
                companyLogoUrl={company.logoUrl}
                email={email}
                emailMode={showOtp ? 'input' : 'hidden'}
                showPlaceholder={showPlaceholder}
                onEmailFocus={() => setShowPlaceholder(false)}
                onEmailBlur={() => setShowPlaceholder(true)}
                onEmailChange={(value) => {
                  setEmail(value);
                  setAuthError('');
                }}
                showOtpAction={showOtp}
                otpButtonLabel="Continue"
                onOtpAction={() => {
                  void handleOtpLogin();
                }}
                showSsoAction={showSso}
                companySlug={company.companySlug ?? companySlug}
                connectionId={company.kindeConnectionId}
                orgCode={company.kindeOrgId}
                isBusy={isSubmittingOtp || kindeLoading}
                busyLabel="Connecting..."
                authError={authError || null}
                onError={(message) => setAuthError(message ?? '')}
              />
            )}

            <div className="mt-10 space-y-3">
              <p className="font-termina text-sm font-bold text-white">
                Need the standard sign-in experience?{' '}
                <Link to="/login" className="underline">
                  Use the default login page.
                </Link>
              </p>
              <p className="font-termina text-sm font-bold text-white">
                New to bQuest?{' '}
                <Link to="/signup" className="underline">
                  Create an account.
                </Link>
              </p>
            </div>
          </section>

          <section className="w-full max-w-[30rem] rounded-2xl p-1 md:max-w-[34rem]">
            <img
              src={LoginImage}
              alt="login background"
              className="max-h-[32rem] w-full rounded-2xl bg-cover bg-center object-cover"
            />
          </section>
        </main>
      </div>
    </div>
  );
};
